import { cn } from '@/lib/utils';

interface VideoSectionProps {
  content: {
    title?: string;
    subtitle?: string;
    videoUrl?: string;
    autoplay?: boolean;
    aspectRatio?: '16:9' | '4:3' | '1:1' | '21:9';
  };
  variant?: string;
  layoutStyle?: string;
}

function getEmbedUrl(url: string): string | null {
  if (!url) return null;
  
  // YouTube — privacy-enhanced mode (youtube-nocookie.com): YouTube doesn't
  // store cookies for the visitor until they play the video. See /cookies.
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&\s]+)/);
  if (youtubeMatch) {
    return `https://www.youtube-nocookie.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo — dnt=1 stops Vimeo tracking the session. See /cookies.
  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?dnt=1`;
  }
  
  // Direct video URL
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return url;
  }
  
  return null;
}

function getAspectRatioClass(ratio: string): string {
  switch (ratio) {
    case '4:3': return 'aspect-[4/3]';
    case '1:1': return 'aspect-square';
    case '21:9': return 'aspect-[21/9]';
    default: return 'aspect-video'; // 16:9
  }
}

export function VideoSection({ content, variant = 'default', layoutStyle = 'classic' }: VideoSectionProps) {
  const {
    title,
    subtitle,
    videoUrl = '',
    autoplay = false,
    aspectRatio = '16:9',
  } = content;

  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideo = videoUrl.match(/\.(mp4|webm|ogg)$/i);
  
  const isMinimal = layoutStyle === 'modern-minimal';
  const isBold = layoutStyle === 'bold-geometric';

  return (
    <section className={cn(
      "section-padding",
      variant === 'fullwidth' ? "px-0" : "",
      variant === 'alt-bg' ? "bg-secondary/30" : "bg-background"
    )}>
      <div className={cn(
        variant === 'fullwidth' ? "" : "container mx-auto px-4"
      )}>
        {(title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-8">
            {title && (
              <h2 className={cn(
                "font-display font-bold text-foreground mb-4",
                isMinimal ? "text-3xl tracking-tight" :
                isBold ? "text-3xl uppercase tracking-wide" :
                "text-3xl md:text-4xl"
              )}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-muted-foreground text-lg">{subtitle}</p>
            )}
          </div>
        )}

        <div className={cn(
          "overflow-hidden mx-auto",
          variant === 'fullwidth' ? "w-full" : "max-w-4xl",
          variant !== 'fullwidth' && (isMinimal ? "rounded-none" : "rounded-2xl"),
          "shadow-xl"
        )}>
          {embedUrl ? (
            isDirectVideo ? (
              <video
                className={cn("w-full", getAspectRatioClass(aspectRatio))}
                controls
                autoPlay={autoplay}
                muted={autoplay}
                playsInline
              >
                <source src={embedUrl} type={`video/${videoUrl.split('.').pop()}`} />
                Your browser does not support the video tag.
              </video>
            ) : (
              <iframe
                src={`${embedUrl}${autoplay ? `${embedUrl.includes('?') ? '&' : '?'}autoplay=1&muted=1` : ''}`}
                className={cn("w-full", getAspectRatioClass(aspectRatio))}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title || 'Video'}
              />
            )
          ) : (
            <div className={cn(
              "w-full flex items-center justify-center bg-muted text-muted-foreground",
              getAspectRatioClass(aspectRatio)
            )}>
              <p>Enter a YouTube, Vimeo, or direct video URL</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
